/**
 * SlipTable Component
 * Table component for displaying slip transactions - all types including
 * duplicates, failed uploads, manual entries, API/LINE/WEB channels
 */

import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Copy, PencilLine, AlertTriangle, XCircle, ArrowDownLeft, ArrowUpRight, ArrowLeftRight, Eye } from "lucide-react"
import { SlipTransaction } from "@/src/types/slip"
import { formatDate, formatCurrency } from "@/src/lib/utils/formatters"

interface SlipTableProps {
  slips: SlipTransaction[]
  loading?: boolean
  pagination: {
    current_page: number
    per_page: number
    total_items: number
    total_pages: number
  }
  onPageChange: (page: number) => void
  onViewDetail: (slip: SlipTransaction) => void
}

// ─── Status badge helpers ────────────────────────────────────────────────────
type StatusStyle = { bg: string; text: string; label: string }

function getStatusStyle(status: string): StatusStyle {
  const normalized = (status || '').toLowerCase()
  switch (normalized) {
    case 'verified':
      return { bg: 'bg-green-100', text: 'text-green-800', label: 'ตรวจสอบแล้ว' }
    case 'failed':
      return { bg: 'bg-red-100', text: 'text-red-800', label: 'ไม่สำเร็จ' }
    case 'duplicate':
      return { bg: 'bg-orange-100', text: 'text-orange-800', label: 'สลิปซ้ำ' }
    case 'pending':
      return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'รอดำเนินการ' }
    case 'cached':
      return { bg: 'bg-blue-100', text: 'text-blue-800', label: 'จากแคช' }
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-600', label: status || 'ไม่ทราบ' }
  }
}

function getValidationErrorLabel(errorType: string | null): string | null {
  if (!errorType) return null
  switch (errorType) {
    case 'correct': return null // correct means no issue
    case 'duplicate': return 'ซ้ำกับรายการเดิม'
    case 'not_found': return 'ไม่พบในธนาคาร'
    case 'damaged': return 'สลิปไม่ชัดเจน'
    case 'expense': return null // expense is shown via transaction_type badge, not as a warning
    case 'incorrect_receiver': return 'ผู้รับไม่ตรง'
    default: return errorType
  }
}

function getErrorCodeLabel(errorCode: string | null): string | null {
  if (!errorCode) return null
  switch (errorCode.toUpperCase()) {
    case 'BANK_API_ERROR':       return 'Bank API ล้มเหลว'
    case 'PROCESSING_ERROR':     return 'เกิดข้อผิดพลาดในการประมวลผล'
    case 'INVALID_FILE':         return 'ไฟล์ไม่ถูกต้อง'
    case 'INVALID_QR':           return 'QR Code ไม่ถูกต้อง'
    case 'TIMEOUT':              return 'การเชื่อมต่อหมดเวลา'
    case 'NETWORK_ERROR':        return 'ไม่สามารถเชื่อมต่อกับธนาคารได้'
    case 'UNSUPPORTED_BANK':     return 'ธนาคารไม่รองรับ'
    case 'IMAGE_QUALITY':        return 'คุณภาพรูปภาพไม่เพียงพอ'
    case 'RATE_LIMIT':           return 'เกินจำนวนคำขอที่อนุญาต'
    case 'FORBIDDEN':            return 'ไม่มีสิทธิ์เข้าถึง'
    default: return null
  }
}

function getTransactionTypeBadge(transactionType: string | null | undefined) {
  const normalized = (transactionType || '').toUpperCase()
  switch (normalized) {
    case 'INCOME':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <ArrowDownLeft className="h-3 w-3" />
          รายรับ
        </span>
      )
    case 'EXPENSE':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <ArrowUpRight className="h-3 w-3" />
          รายจ่าย
        </span>
      )
    case 'TRANSFER':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <ArrowLeftRight className="h-3 w-3" />
          โอน
        </span>
      )
    default:
      return <span className="text-xs text-gray-400">-</span>
  }
}

function getChannelBadge(channel: string | undefined, recordSource: string | undefined) {
  if (recordSource === 'manual_entry') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
        <PencilLine className="h-3 w-3" />
        Manual
      </span>
    )
  }
  switch ((channel || '').toUpperCase()) {
    case 'API':
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">API</span>
    case 'LINE':
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">LINE</span>
    case 'MANUAL':
    default:
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">เว็บ</span>
  }
}

// ─── Component ───────────────────────────────────────────────────────────────
export function SlipTable({ slips, loading, pagination, onPageChange, onViewDetail }: SlipTableProps) {
  const handleFirstPage = () => onPageChange(1)
  const handlePrevPage  = () => onPageChange(Math.max(1, pagination.current_page - 1))
  const handleNextPage  = () => onPageChange(Math.min(pagination.total_pages, pagination.current_page + 1))
  const handleLastPage  = () => onPageChange(pagination.total_pages)

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4" />
            <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!slips || slips.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-gray-500 text-lg mb-2">ไม่พบข้อมูลสลิป</p>
            <p className="text-gray-400 text-sm">กรุณาเพิ่มสลิปใหม่หรือเปลี่ยนเงื่อนไขการค้นหา</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-gray-700 min-w-[60px]">#</TableHead>
              <TableHead className="text-gray-700 min-w-[140px]">วันที่ส่งตรวจ</TableHead>
              <TableHead className="text-gray-700 min-w-[100px]">สาขา</TableHead>
              <TableHead className="text-gray-700 min-w-[140px]">ผู้โอน</TableHead>
              <TableHead className="text-gray-700 min-w-[140px]">ผู้รับ</TableHead>
              <TableHead className="text-gray-700 text-right min-w-[100px]">จำนวนเงิน</TableHead>
              <TableHead className="text-gray-700 min-w-[90px]">ประเภท</TableHead>
              <TableHead className="text-gray-700 min-w-[80px]">ช่องทาง</TableHead>
              <TableHead className="text-gray-700 min-w-[130px]">สถานะ</TableHead>
              <TableHead className="text-gray-700 text-center min-w-[80px]">จัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {slips.map((slip, index) => {
              const statusStyle = getStatusStyle(slip.verification_status)
              const errorLabel  = getValidationErrorLabel(slip.validation_error_type)
              const rowNum = (pagination.current_page - 1) * pagination.per_page + index + 1

              return (
                <TableRow
                  key={slip.id}
                  className={slip.is_duplicate ? 'bg-orange-50/40' : undefined}
                >
                  {/* # */}
                  <TableCell className="font-medium text-gray-500">{rowNum}</TableCell>

                  {/* Date */}
                  <TableCell className="text-sm">{formatDate(slip.created_at)}</TableCell>

                  {/* Branch */}
                  <TableCell className="text-sm">
                    {slip.branch_name || slip.branch_id || '-'}
                  </TableCell>

                  {/* Sender */}
                  <TableCell className="text-sm">
                    <div>{slip.sender_name || '-'}</div>
                    {slip.sender_account && (
                      <div className="text-xs text-gray-400">{slip.sender_account}</div>
                    )}
                  </TableCell>

                  {/* Receiver */}
                  <TableCell className="text-sm">
                    <div>{slip.receiver_name || '-'}</div>
                    {slip.account_number_display && (
                      <div className="text-xs text-gray-400">{slip.account_number_display}</div>
                    )}
                  </TableCell>

                  {/* Amount */}
                  <TableCell className="text-right font-medium">
                    {formatCurrency(slip.amount)}
                  </TableCell>

                  {/* Transaction type (income / expense) */}
                  <TableCell>
                    {getTransactionTypeBadge(slip.transaction_type)}
                  </TableCell>

                  {/* Channel */}
                  <TableCell>
                    {getChannelBadge(slip.channel, slip.record_source)}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <div className="flex flex-col gap-1 items-start">
                      {/* Duplicate warning strip */}
                      {slip.is_duplicate && (
                        <span className="inline-flex items-center gap-1 text-xs text-orange-600 font-medium">
                          <Copy className="h-3 w-3" />
                          ซ้ำ
                        </span>
                      )}

                      {/* Main status badge */}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                        {statusStyle.label}
                      </span>

                      {/* Validation error detail */}
                      {errorLabel && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <AlertTriangle className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                          {errorLabel}
                        </span>
                      )}

                      {/* Error code label for failed verifications (when no validation_error_type) */}
                      {slip.verification_status === 'failed' && !errorLabel && (
                        <span className="inline-flex items-center gap-1 text-xs text-red-500">
                          <XCircle className="h-3 w-3 flex-shrink-0" />
                          {getErrorCodeLabel(slip.error_code) ?? 'ตรวจสอบล้มเหลว'}
                        </span>
                      )}

                      {/* Error message for failed verifications */}
                      {slip.error_message && slip.verification_status === 'failed' && (
                        <span
                          className="text-xs text-red-400 leading-tight break-words whitespace-normal max-w-[200px] line-clamp-2"
                          title={slip.error_message}
                        >
                          {slip.error_message}
                        </span>
                      )}
                    </div>
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-500 hover:text-brand-primary"
                      onClick={() => onViewDetail(slip)}
                      title="ดูรายละเอียด"
                      aria-label="ดูรายละเอียดสลิป"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-center gap-2 py-4 border-t">
        <Button
          variant="ghost" size="icon" className="h-8 w-8 text-gray-400"
          onClick={handleFirstPage}
          disabled={pagination.current_page === 1}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost" size="icon" className="h-8 w-8 text-gray-400"
          onClick={handlePrevPage}
          disabled={pagination.current_page === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button size="icon" className="h-8 w-8 bg-brand-primary hover:bg-brand-primary/90 text-white">
          {pagination.current_page}
        </Button>
        <Button
          variant="ghost" size="icon" className="h-8 w-8 text-gray-400"
          onClick={handleNextPage}
          disabled={pagination.current_page === pagination.total_pages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost" size="icon" className="h-8 w-8 text-gray-400"
          onClick={handleLastPage}
          disabled={pagination.current_page === pagination.total_pages}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Pagination info */}
      <div className="flex items-center justify-center pb-4 text-sm text-gray-500">
        แสดง {(pagination.current_page - 1) * pagination.per_page + 1}–
        {Math.min(pagination.current_page * pagination.per_page, pagination.total_items)} จาก{' '}
        {pagination.total_items} รายการ
      </div>
    </div>
  )
}
